import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useLocation, useNavigate } from "react-router";
import { formatClock } from "../../domain/duration";
import { dayBandPosition, dayBandSegments, dayBandWindow } from "../../domain/dayBand";
import type { DayBandInterval } from "../../domain/dayBand";
import { toEpochSeconds } from "../../domain/time";
import { useI18n } from "../../shared/i18n/I18nContext";
import { hasAccessRight } from "../../shared/auth/accessRights";
import { useNavigationRoles } from "../../shared/session/useNavigationRoles";
import { Alert } from "../../shared/ui/Alert";
import { cx } from "../../shared/ui/classNames";
import { formatLocalDate, formatTime } from "../../shared/ui/format";
import { DayBand } from "../../shared/ui/DayBand";
import { Loading } from "../../shared/ui/Loading";
import { PageHeader } from "../../shared/ui/PageHeader";
import styles from "../../shared/ui/ui.module.css";
import { SetupBanner } from "../setup/SetupBanner";
import { DayOverview } from "../projects/DayOverview";
import { segmentsFromLocalDay } from "../projects/model/daySegments";
import { RejectedAttributions } from "../projects/RejectedAttributions";
import clockStyles from "./ClockPage.module.css";
import { ConflictList } from "./ConflictList";
import { CrewClock } from "./CrewClock";
import { EarlyStartConfirmation } from "./EarlyStartConfirmation";
import { LocationNotice } from "./LocationNotice";
import { readDayOverviewRequest } from "./model/dayOverviewHandover";
import { NextShift } from "./NextShift";
import { ClockNfc } from "./NfcClock";
import { ClockProjectSwitcher } from "./ProjectSwitcher";
import { TodayOverview } from "./TodayOverview";
import { useClockControl } from "./useClockControl";
import { WarningList } from "./WarningList";

/** Interval of the live durations of the start page. */
const pageTickMs = 1_000;

/**
 * The start page „Heute“ (F-75, spec 11.1): one-tap clock actions, live durations, warnings and
 * conflicts as before, then the next shift, the balance and what is open (own requests, findings,
 * missing project attributions). The clock works offline on the local state (spec 8.1); the
 * overview comes from the existing queries and needs the server. The clocking itself is the shared
 * {@link useClockControl}, which the compact clock of the app bar uses too.
 *
 * @returns The page.
 */
export function ClockPage(): JSX.Element {
  const { t, languageTag } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  // After a clock-out from the compact clock the day overview opens here (spec 6.5.4, P-2).
  const requestedOverview = readDayOverviewRequest(location.state);
  const control = useClockControl({ tickMs: pageTickMs, initialOverviewDate: requestedOverview });
  // The overview handed over by the compact clock takes the focus once (F-75).
  const [focusOverview] = useState(requestedOverview !== null);
  // Last known roles, so that the crew is offered offline too (T-170, ADR-0104).
  const recordsForOthers = hasAccessRight(useNavigationRoles() ?? [], "ClockForOthers");
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const { pathname, search, hash } = location;

  // The request is read once; reloading or going back does not open the overview again.
  useEffect(() => {
    if (requestedOverview !== null) {
      void navigate({ pathname, search, hash }, { replace: true, state: null });
    }
  }, [requestedOverview, navigate, pathname, search, hash]);

  if (control === null) {
    const loading = <Loading />;
    return loading;
  }

  const { clock, day, now, today, timeZone, snapshot, timeline } = control;
  // The day band (F-43, ADR-0159): the blocks of the working day on the hours from 06:00 to 18:00.
  const bandIntervals: DayBandInterval[] =
    day === null
      ? []
      : day.blocks.map((block) => ({ kind: block.kind, start: block.start, end: block.end }));
  const bandWindow = day === null ? null : dayBandWindow(day.date, bandIntervals, timeZone);
  const bandSegments = bandWindow === null ? [] : dayBandSegments(bandIntervals, bandWindow);
  const bandNow =
    bandWindow === null || clock.state === "Off" ? null : dayBandPosition(now, bandWindow);
  const { overviewDate, overviewDay } = control;
  const projectSwitcher = <ClockProjectSwitcher control={control} />;

  const page = (
    <div className={styles["page"]}>
      <PageHeader title={t("nav.clock")} />
      <SetupBanner />

      <section
        className={cx(styles["card"], clockStyles["hero"])}
        aria-label={
          day === null
            ? control.statusText
            : t("clock.dayOf", { date: formatLocalDate(day.date, languageTag) })
        }
      >
        {/* The current time is large and silent: the status line announces the changes (F-43). */}
        <p className={clockStyles["clockTime"]} aria-hidden="true">
          {formatTime(now, languageTag, timeZone)}
        </p>
        <p className={clockStyles["status"]} aria-live="polite">
          <span className={clockStyles["stateDot"]} data-state={clock.state} aria-hidden="true" />
          {control.statusText}
        </p>
        {day === null || bandWindow === null ? null : (
          <>
            <p className={styles["muted"]}>
              {t("clock.dayOf", { date: formatLocalDate(day.date, languageTag) })}
            </p>
            <DayBand
              segments={bandSegments}
              startHour={bandWindow.startHour}
              endHour={bandWindow.endHour}
              nowPercent={bandNow}
            />
            <dl className={clockStyles["figures"]}>
              <div>
                <dt>{t("clock.workToday")}</dt>
                <dd>{formatClock(day.workSeconds)}</dd>
              </div>
              <div>
                <dt>{t("clock.breakToday")}</dt>
                <dd>{formatClock(day.breakSeconds)}</dd>
              </div>
            </dl>
          </>
        )}
      </section>

      <NextShift nowMs={control.nowMs} />

      {/* The project for the next clock-in is chosen before "Kommen" (UX-002). */}
      {clock.state === "Off" ? projectSwitcher : null}

      <div className={clockStyles["actions"]}>
        {control.primaryActions.map((action, index) => (
          <button
            key={action.kind}
            ref={index === 0 ? primaryActionRef : undefined}
            type="button"
            disabled={control.busy}
            className={cx(
              styles["button"],
              clockStyles["action"],
              index === 0 ? styles["primary"] : undefined,
            )}
            onClick={() => {
              control.onAction(action);
            }}
          >
            {control.actionLabel(action)}
          </button>
        ))}
      </div>

      {/* Feedback directly below the action, above the fold on small phones (UX-004). */}
      {control.confirmEarlyStart && control.earliestStart !== null ? (
        <EarlyStartConfirmation
          restEnd={control.timeText(control.earliestStart)}
          onConfirm={() => {
            control.onAction({ kind: "ClockIn", breakQualification: null });
          }}
          onCancel={() => {
            control.cancelEarlyStart();
            primaryActionRef.current?.focus();
          }}
        />
      ) : null}
      {control.noticeAction === null || control.location.policy === null ? null : (
        <LocationNotice
          policy={control.location.policy}
          onConfirm={control.confirmLocationNotice}
          onWithoutLocation={control.recordWithoutLocation}
          onDismiss={() => {
            control.dismissLocationNotice();
            primaryActionRef.current?.focus();
          }}
        />
      )}
      {control.location.locating ? (
        <p role="status" className={cx(styles["muted"], clockStyles["footnote"])}>
          {t("location.locating")}
        </p>
      ) : null}
      {control.recordError === null ? null : <Alert tone="danger">{control.recordError}</Alert>}
      {control.location.policy === null ? null : (
        <p className={cx(styles["muted"], clockStyles["footnote"])}>
          {t("location.clockHint", { purpose: control.location.policy.purpose ?? "" })}
        </p>
      )}

      {control.standbyAction === undefined ? null : (
        <div className={styles["stack"]}>
          <button
            type="button"
            disabled={control.busy}
            className={styles["button"]}
            onClick={() => {
              if (control.standbyAction !== undefined) {
                control.onAction(control.standbyAction);
              }
            }}
          >
            {control.actionLabel(control.standbyAction)}
          </button>
          <p className={cx(styles["muted"], clockStyles["footnote"])}>{t("clock.breakHint")}</p>
        </div>
      )}

      {control.clockOutAction === undefined ? null : (
        <div className={clockStyles["clockOut"]}>
          <button
            type="button"
            disabled={control.busy}
            className={cx(styles["button"], clockStyles["action"], clockStyles["clockOutButton"])}
            onClick={() => {
              if (control.clockOutAction !== undefined) {
                control.onAction(control.clockOutAction);
              }
            }}
          >
            {control.actionLabel(control.clockOutAction)}
          </button>
        </div>
      )}

      <ClockNfc control={control} />

      {clock.state === "Off" ? null : projectSwitcher}

      {overviewDate === null || overviewDay === undefined ? null : (
        <DayOverview
          date={overviewDate}
          timeZone={timeZone}
          segments={segmentsFromLocalDay(
            overviewDay,
            snapshot?.projectDays?.find((item) => item.date === overviewDate) ?? null,
            control.attributions,
          )}
          projects={control.projects}
          choices={control.projectChoices}
          busy={control.busy}
          onAssign={control.assignRange}
          activitiesOf={control.activitiesOf}
          requireActivity={control.requireActivity}
          onClose={control.closeOverview}
          autoFocus={focusOverview}
        />
      )}

      <WarningList
        warnings={control.warnings}
        timeZone={timeZone}
        today={today}
        continuousLimitSeconds={control.limits.break.maxContinuousWorkSeconds}
      />

      <ConflictList conflicts={control.conflicts} />
      <RejectedAttributions timeZone={timeZone} />

      <TodayOverview today={today} timeZone={timeZone} />

      {snapshot === null ? (
        <Alert tone="info">{t("clock.snapshotMissing")}</Alert>
      ) : (
        <p className={cx(styles["muted"], clockStyles["footnote"])}>
          {t("clock.snapshotFrom", {
            time: control.timeText(toEpochSeconds(snapshot.fetchedAtMs)),
          })}
        </p>
      )}
      {timeline.isConsistent ? null : <Alert tone="warning">{t("clock.inconsistent")}</Alert>}
      {recordsForOthers ? (
        <CrewClock owner={control.owner} timeZone={timeZone} today={today} />
      ) : null}
    </div>
  );

  return page;
}
